// Per-project permissions — the client half of project visibility/powers.
//
// Mirrors backend/shared/dealAuthz.js and the generated Firestore rules:
// the org owner holds every power on every project; org admins do too except
// on `restricted` projects, where they count as plain members; plain members
// need a participant doc whose dealRole preset (registry DEAL_ROLE_POWERS) is
// overridden per-key by participant.powers. The server re-checks everything;
// this module exists so the UI can hide/disable what a user can't do.
import { useEffect, useState, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useOrg } from './orgs';
import { useEntityList } from './entities';
import registry from './entityRegistry.gen.json';

export const DEAL_ROLES = registry.dealRoles;
export const DEAL_POWERS = registry.dealPowers;
export const DEAL_ROLE_POWERS = registry.dealRolePowers;
// Default grant when adding someone to a project. Editor is the working
// role: upload, edit, and natural-language agent requests. Viewer/commenter
// exist for later tightening — they are never the onboarding default.
export const DEFAULT_DEAL_ROLE = 'editor';

export const POWER_LABELS = {
  download: 'Download',
  upload: 'Upload',
  comment: 'Comment',
  edit: 'Edit',
  agent: 'AI requests',
  manage: 'Manage people',
};

const ALL_POWERS = Object.fromEntries(DEAL_POWERS.map((p) => [p, true]));

export const effectivePowers = (dealRole, overrides) => ({
  ...(DEAL_ROLE_POWERS[dealRole] || DEAL_ROLE_POWERS.viewer),
  ...(overrides || {}),
});

// Operator flag from the verified ID token (`superadmin` custom claim, set
// only through the admin SDK). Gates the Brain console; the server and the
// Firestore rules re-check the same claim.
export function useSuperadmin() {
  const { user } = useAuth();
  const [on, setOn] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!user) { setOn(false); return undefined; }
    user.getIdTokenResult?.().then((r) => { if (alive) setOn(r?.claims?.superadmin === true); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);
  return on;
}

// My grant on a project → { loading, orgRole, dealRole, participant, powers,
// can(power) }. The owner short-circuits to every power; admins do unless
// the project is restricted (then their participant doc, if any, governs).
export function useDealPowers(dealId) {
  const { user } = useAuth();
  const { orgId, role: orgRole } = useOrg();
  const isOwner = orgRole === 'owner';
  const isAdmin = orgRole === 'admin';
  const [participant, setParticipant] = useState(undefined); // undefined = loading
  const [restricted, setRestricted] = useState(undefined); // admins only; undefined = loading

  useEffect(() => {
    if (!user || !orgId || !dealId || !isAdmin) {
      setRestricted(false);
      return undefined;
    }
    const unsub = db.doc(`orgs/${orgId}/deals/${dealId}`).onSnapshot(
      (snap) => setRestricted(snap.exists ? snap.data().restricted === true : false),
      // Denied read = restricted project we are not on.
      () => setRestricted(true)
    );
    return () => unsub();
  }, [user, orgId, dealId, isAdmin]);

  useEffect(() => {
    if (!user || !orgId || !dealId || isOwner) {
      setParticipant(null);
      return undefined;
    }
    const unsub = db.doc(`orgs/${orgId}/deals/${dealId}/participants/${user.uid}`).onSnapshot(
      (snap) => setParticipant(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setParticipant(null)
    );
    return () => unsub();
  }, [user, orgId, dealId, isOwner]);

  return useMemo(() => {
    const isOrgAdmin = isOwner || (isAdmin && restricted === false);
    if (isOrgAdmin) {
      return {
        loading: false,
        orgRole,
        dealRole: 'admin',
        participant: null,
        powers: { ...ALL_POWERS },
        can: () => true,
      };
    }
    const loading = participant === undefined || (isAdmin && restricted === undefined);
    const powers = participant ? effectivePowers(participant.dealRole, participant.powers) : {};
    return {
      loading,
      orgRole,
      dealRole: participant?.dealRole || null,
      participant: participant || null,
      powers,
      can: (p) => Boolean(powers[p]),
    };
  }, [isOwner, isAdmin, restricted, orgRole, participant]);
}

// Projects the current user may see — one query shape per rules allow line:
// the owner gets the unfiltered list; admins get where('restricted','==',
// false) PLUS the projects they were added to (restricted ones included);
// plain members get the memberUids-filtered query.
export function useVisibleDeals() {
  const { user } = useAuth();
  const { orgId, role } = useOrg();
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';
  const isOrgAdmin = isOwner || isAdmin;
  const scope = orgId ? { org: orgId } : null;
  const ready = !!orgId && !!role && !!user;
  const { docs: ownerDocs, error: e1 } = useEntityList('deal', scope, { enabled: ready && isOwner });
  const { docs: openDocs, error: e2 } = useEntityList('deal', scope, {
    enabled: ready && isAdmin, orderBy: null, where: [['restricted', '==', false]],
  });
  const { docs: mineDocs, error: e3 } = useEntityList('deal', scope, {
    enabled: ready && !isOwner, orderBy: null, where: [['memberUids', 'array-contains', user?.uid || '-']],
  });
  // Filtered queries can't order server-side without a composite index —
  // merge, dedupe, and normalize to createdAt desc for every path.
  const sorted = useMemo(() => {
    let docs;
    if (isOwner) docs = ownerDocs;
    else if (isAdmin) docs = openDocs && mineDocs ? [...openDocs, ...mineDocs] : null;
    else docs = mineDocs;
    if (!docs) return docs;
    const seen = new Set();
    return docs
      .filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [isOwner, isAdmin, ownerDocs, openDocs, mineDocs]);
  return { deals: sorted, error: e1 || e2 || e3, isOrgAdmin };
}

// Projects this user is actually on (uid in memberUids), including when they
// are an org admin. Admins can open every project; tag chips and other
// "my work" surfaces still use this list so an admin does not see tags from
// projects they were never added to. Live: the query is a snapshot.
export function useMemberDeals({ enabled = true } = {}) {
  const { user } = useAuth();
  const { orgId, role } = useOrg();
  const opts = {
    enabled: enabled && !!orgId && !!role && !!user,
    orderBy: null,
    where: [['memberUids', 'array-contains', user?.uid || '-']],
  };
  const { docs, error } = useEntityList('deal', orgId ? { org: orgId } : null, opts);
  const sorted = useMemo(
    () => (docs ? [...docs].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) : docs),
    [docs]
  );
  return { deals: sorted, error };
}
